import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const ProgressBar = ({
    current = 102,
    rangeStart = 40,
    rangeEnd = 180,
    start = 55,
    end = 102,
    height = '10px',
    showCurrentIndicator = true,
    showProgress = true,
    showStartEndValues = false, // 시작/끝지점 수치 표시 여부
    startLabel = rangeStart, // 시작값
    endLabel = rangeEnd, // 끝값
    startValue = null, // 시작값 표시용
    endValue = null, // 끝값 표시용
    showValueLabel = false,
    valueLabelPosition = 'center',
    valueLabel = '',
    backgroundColor,
    progressColor = '#FF00D0',
    indicatorColor = 'white',
    borderRadius = '10px',
    progressSx = {},
    startEndLabelSx = {},
    valueLabelSx = {},
    sx = {},
    ...props
}) => {
    const theme = useTheme();

    backgroundColor = backgroundColor || theme.palette.figma.white[20];

    // 현재 심박수가 전체 범위에서 차지하는 위치 계산
    const currentPosition =
        ((current - rangeStart) / (rangeEnd - rangeStart)) * 100;

    // 진행률 계산 (progressPercentage가 없으면 start~end 범위 기준으로 계산)
    const calculatedProgress = ((end - start) / (rangeEnd - rangeStart)) * 100;

    return (
        <Box
            sx={{
                width: '100%',
                position: 'relative',
                ...sx,
            }}
            {...props}
        >
            {/* 프로그레스 바 */}
            <Box
                sx={{
                    height,
                    width: '100%',
                    position: 'relative',
                    backgroundColor,
                    borderRadius,
                }}
            >
                {/* 배경 (전체 범위) */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius,
                        overflow: 'hidden',
                    }}
                >
                    {showProgress && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: `${((start - rangeStart) / (rangeEnd - rangeStart)) * 100}%`,
                                width: `${calculatedProgress}%`,
                                height: '100%',
                                borderRadius,
                                backgroundColor: progressColor,
                                ...progressSx,
                            }}
                        > {showValueLabel && valueLabelPosition == 'right' && (
                            <Typography sx={{
                                position: 'absolute',
                                top: '50%',
                                right: 0,
                                transform: 'translate(-50%, -50%)',
                                fontSize: '16px',
                                color: theme.palette.figma.buttonText,
                                textAlign: 'right',
                                fontStyle: 'normal',
                                fontWeight: 700,
                                lineHeight: 'normal',
                                letterSpacing: '-0.48px',
                                ...valueLabelSx,
                            }}>
                                {valueLabel !== '' ? valueLabel : `${end}/${rangeEnd}`}
                            </Typography>
                        )}
                        </Box>
                    )}
                    {/* 현재 심박수 위치 표시 */}
                    {showCurrentIndicator && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: `${currentPosition}%`,
                                width: '14px',
                                height: '100%',
                                borderRadius,
                                backgroundColor: indicatorColor,
                                transform: 'translateX(-50%)',
                            }}
                        />
                    )}
                    {showValueLabel && valueLabelPosition === 'center' && (
                        <Typography sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: '12px',
                            color: theme.palette.figma.buttonText,
                            textAlign: 'center',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            lineHeight: 'normal',
                            letterSpacing: '-0.36px',
                            ...valueLabelSx,
                        }}>
                            {valueLabel !== '' ? valueLabel : `${end}/${rangeEnd}`}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* 시작/끝지점 수치 표시 */}
            {showStartEndValues && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '10px',
                    width: '100%'
                }}>
                    <Typography sx={{
                        fontSize: '12px',
                        color: theme.palette.figma.white[60],
                        fontWeight: 400,
                        letterSpacing: '-0.36px',
                        ...startEndLabelSx,
                    }}>
                        {startValue !== null ? startValue : (startLabel !== null ? startLabel.toLocaleString() : '')}
                    </Typography>
                    <Typography sx={{
                        fontSize: '12px',
                        color: theme.palette.figma.white[60],
                        fontWeight: 400,
                        letterSpacing: '-0.36px',
                        ...startEndLabelSx,
                    }}>
                        {endValue !== null ? endValue : (endLabel !== null ? endLabel.toLocaleString() : '')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ProgressBar;
